import DocumentPage from './DocumentPage';

export const admissionDocument = {
  slug: 'admission',
  route: '/admission',
  title: 'Admission',
  category: 'Academics',
  format: 'DOCX',
  description:
    'Step-by-step admission documentation with screenshots for register setup, application capture, workflow stages, and student profile handoff.',
  metrics: [
    { label: 'Primary focus', value: 'Admission intake' },
    { label: 'Doc format', value: 'Guided screenshots' },
    { label: 'Coverage', value: 'Register to student profile' },
  ],
  workflow: [
    'Create an admission register and define the course, dates, fees, limits, and eligibility settings.',
    'Move the register from Draft into Confirmed and Application Gathering so applications can be collected.',
    'Create the student application, complete the tabs, and progress it through Submitted, Confirmed, Admission Confirm, and Enroll.',
  ],
  sections: [
    {
      id: 'admission-register',
      title: 'Admission Register Setup',
      body: [
        'The admission process begins with an admission register. This is the control record that defines the intake window, class, fee setup, student limits, and minimum age.',
        'Use this section when you want to configure a new admission cycle before any student applications are entered.',
      ],
      steps: [
        'Open Admissions and go to Admission Registers.',
        'Click New to create a new register.',
        'Complete the main fields and save the record.',
        'Use the workflow buttons to move the register into the correct stage for your intake cycle.',
      ],
      figure: {
        src: '/assets/doc-images/admission/image2.png',
        alt: 'Admission register form in draft state',
        caption: 'A new admission register in Draft state before the intake process begins.',
      },
      detailCards: [
        {
          title: 'Core register fields',
          bullets: [
            'Name: set a clear register title for the intake.',
            'Course and Class: pick the academic target for the admission cycle.',
            'Start Date and End Date: define the admission window.',
            'Course Fees and Terms: connect the financial structure for the intake.',
          ],
        },
        {
          title: 'Capacity and eligibility',
          bullets: [
            'Minimum No. of Admission: define the minimum intake target.',
            'Maximum No. of Admission: control the upper student limit.',
            'Minimum Required Age: enforce the age qualification for applicants.',
            'Academic Year: keep the register aligned with the correct school cycle.',
          ],
        },
      ],
      gallery: [
        {
          src: '/assets/doc-images/admission/image3.png',
          alt: 'Admission register in confirmed state',
          caption: 'Confirmed state with Start Application available.',
        },
        {
          src: '/assets/doc-images/admission/image4.png',
          alt: 'Admission register during application gathering',
          caption: 'Application Gathering stage for receiving applications.',
        },
        {
          src: '/assets/doc-images/admission/image5.png',
          alt: 'Admission register during admission process',
          caption: 'Admission Process stage after the intake moves forward.',
        },
        {
          src: '/assets/doc-images/admission/image6.png',
          alt: 'Admission register in done state',
          caption: 'Done state after the admission cycle is completed.',
        },
      ],
    },
    {
      id: 'student-application',
      title: 'Create the Student Application',
      body: [
        'After the register is ready, create the application record for each student. This form captures the student identity, admission details, educational history, and personal data.',
        'The screenshots below show the registration form and the main tabs that need to be completed during admission entry.',
      ],
      steps: [
        'Open Admissions and go to Applications.',
        'Click New to create the application for a student.',
        'Add the student title, first name, middle name, last name, and photo.',
        'Complete each tab before moving the workflow forward.',
      ],
      figure: {
        src: '/assets/doc-images/admission/image7.png',
        alt: 'Admission application form with admission detail tab',
        caption: 'Application form with the Admission Detail tab selected.',
      },
      subsections: [
        {
          id: 'admission-detail-tab',
          title: 'Admission Detail Tab',
          body: [
            'This tab links the student to the selected admission register and stores the application-level finance and identity values used during confirmation.',
          ],
          bullets: [
            'Admission Register: links the application to the intake record.',
            'Application Number and Application Date: system-generated admission tracking fields.',
            'Admission Date, Due Date, Discount, Fees, and Fees Term: control the financial side of enrollment.',
            'Partner and category values connect the student to the broader operational record.',
          ],
          figure: {
            src: '/assets/doc-images/admission/image7.png',
            alt: 'Admission detail tab screenshot',
            caption: 'Admission Detail tab for the student application form.',
          },
        },
        {
          id: 'educational-detail-tab',
          title: 'Educational Detail Tab',
          body: [
            'Use this page to capture the student previous institute, prior course, and earlier score or result before confirming admission.',
          ],
          bullets: [
            'Previous Institute: the last school or college attended.',
            'Previous Course: the course completed before admission.',
            'Previous Result: the performance or percentage obtained in the prior course.',
          ],
          figure: {
            src: '/assets/doc-images/admission/image8.png',
            alt: 'Educational detail tab screenshot',
            caption: 'Educational Detail tab showing prior institute and prior result data.',
          },
        },
        {
          id: 'personal-detail-tab',
          title: 'Personal Detail Tab',
          body: [
            'This page stores the applicant personal profile, communication fields, date of birth, address, and family details used during processing.',
          ],
          bullets: [
            'Gender, Email, Phone, and Mobile capture the direct communication fields.',
            'Birth Date and Address capture the identity and location details.',
            'Family Business and Family Income capture supporting household information.',
          ],
          figure: {
            src: '/assets/doc-images/admission/image9.png',
            alt: 'Personal detail tab screenshot',
            caption: 'Personal Detail tab with contact, birth date, address, and family fields.',
          },
        },
      ],
    },
    {
      id: 'application-workflow',
      title: 'Application Workflow States',
      body: [
        'The application follows a controlled workflow so the team can clearly see whether the record is still being prepared, reviewed, confirmed, or fully enrolled.',
        'Each button below matches the progression shown in the source admission document.',
      ],
      gallery: [
        {
          src: '/assets/doc-images/admission/imagea.png',
          alt: 'Submit stage screenshot',
          caption: 'Draft to Submitted using the Submit button.',
        },
        {
          src: '/assets/doc-images/admission/imageb.png',
          alt: 'Confirm stage screenshot',
          caption: 'Submitted to Confirmed using the Confirm button.',
        },
        {
          src: '/assets/doc-images/admission/imagec.png',
          alt: 'Admission confirm screenshot',
          caption: 'Confirmed to Admission Confirm using the Admission Confirm button.',
        },
        {
          src: '/assets/doc-images/admission/imaged.png',
          alt: 'Enroll stage screenshot',
          caption: 'Admission Confirm to Done using the Enroll button.',
        },
        {
          src: '/assets/doc-images/admission/imagee.png',
          alt: 'Open student profile screenshot',
          caption: 'Done state with Open Student Profile available.',
        },
      ],
      subsections: [
        {
          id: 'draft-to-submitted',
          title: 'Draft to Submitted',
          bullets: [
            'Save the application first so it enters Draft state.',
            'Click Submit to move the record from Draft to Submitted.',
          ],
          figure: {
            src: '/assets/doc-images/admission/imagea.png',
            alt: 'Draft to submitted workflow screenshot',
            caption: 'Draft state with the Submit action available.',
          },
        },
        {
          id: 'submitted-to-confirmed',
          title: 'Submitted to Confirmed',
          bullets: [
            'Review the application details after submission.',
            'Click Confirm to move the application into Confirmed state.',
          ],
          figure: {
            src: '/assets/doc-images/admission/imageb.png',
            alt: 'Submitted to confirmed workflow screenshot',
            caption: 'Submitted state ready for confirmation.',
          },
        },
        {
          id: 'confirmed-to-admission-confirm',
          title: 'Confirmed to Admission Confirm',
          bullets: [
            'Use Admission Confirm once the application is approved for admission.',
            'This marks the record as ready for the final enrollment step.',
          ],
          figure: {
            src: '/assets/doc-images/admission/imagec.png',
            alt: 'Admission confirm workflow screenshot',
            caption: 'Confirmed state with the Admission Confirm action.',
          },
        },
        {
          id: 'admission-confirm-to-enrolled',
          title: 'Admission Confirm to Done',
          bullets: [
            'Click Enroll to convert the admission into a completed enrollment.',
            'After enrollment, the record reaches Done state.',
          ],
          figure: {
            src: '/assets/doc-images/admission/imaged.png',
            alt: 'Enroll workflow screenshot',
            caption: 'Enroll action that completes the application workflow.',
          },
        },
      ],
    },
    {
      id: 'student-profile-handoff',
      title: 'Open the Student Profile',
      body: [
        'Once the application is enrolled, the process continues into the student record. The Open Student Profile action gives the admissions team a direct handoff into the operational student profile.',
      ],
      figure: {
        src: '/assets/doc-images/admission/imagef.png',
        alt: 'Student profile opened from the admission workflow',
        caption: 'Student profile page opened after the admission is fully enrolled.',
      },
      bullets: [
        'Use the generated student profile for personal information, attendance, activities, and further academic operations.',
        'This step completes the journey from admission intake to a usable student record.',
      ],
    },
  ],
  related: ['student', 'parent', 'faculty'],
};

export default function AdmissionPage() {
  return <DocumentPage document={admissionDocument} />;
}
